import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import axiosInstant from "../../../../../api";
import {
  createProductPdpUrl,
  deleteProductPdpUrl,
  generateProductPdpUrlJsonLd,
  getFileIconikJsonLd,
  listProductPdpUrls,
  productPdpUrlVideo,
  requestProductPdpUrlScore,
  rescoreProductPdpUrl,
  updateProductPdpUrl,
} from "../../../../../api/endpoints";
import { usePendingScorePoll } from "../../../../../components/PdpScoreStatus/PdpScoreStatus";
import type { PdpUrlAttempt, PdpUrlScore } from "../../../../../components/PdpScoreStatus/PdpScoreStatus";
import { markupSignature, watchMarkupChange } from "../../../../../components/PdpPages/markupWatch";
import type { PdpUrlVideoLink } from "../../../../../components/PdpPages/PdpJsonLdOutput";
import { TOKEN } from "../../../../../store/constant";
import { scoringLimitMessage } from "../../../../../utils/plans";
import { isSessionRedirect } from "../pageModel";
import { useMountedRef } from "../components/useMountedRef";
import { createLoadSequence } from "./loadSequence";
import { PENDING_POLL_MS, addedPageId, intervalErrorMessage, isAddedPaused, refusalFor } from "./pageModel";
import type { PdpUrl } from "./pageModel";
import { useBusyKeys } from "./useBusyKeys";

/**
 * Product pages & audits — the list and every page action.
 *
 * The settings modal keeps its own code (D3); this is the page's copy of it.
 * Every write calls the SAME endpoint with the SAME body as
 * MODAL/ProductPdpUrlsPanel.tsx, and each one cites its source lines. The
 * strings in the toasts are the modal's. What differs is where a result
 * lands: refusals become an inline line on the page block (the plan's
 * "refused" modifier) instead of a toast, and a failed load is an error
 * state instead of an empty list (:215-219 toasts and sets []).
 *
 * The section shows one block per page (as tabs at 2+ pages), and the hook
 * serves every page at once: the Score now lock, refusals and busy flags are
 * by page id.
 *
 * Loads go through `createLoadSequence` (loadSequence.ts), which decides which
 * response is shown and what each caller gets back, so a local patch is never
 * undone by a load that read the server before the write, and a write's
 * re-read is never dropped with nothing after it. `silentFetch` keeps the
 * modal's `fetchRows` contract (:208-222) — resolves to fresh rows, or null on
 * a failure — which is what `watchMarkupChange` (markupWatch.ts:34,54-58) and
 * `usePendingScorePoll` (PdpScoreStatus.tsx:148-163) call. Fresh = from a load
 * that started after the call.
 *
 * `pollPaused` (the settings modal is open) stops the pending poll: the modal
 * runs its own, identical one over the same list. Closing it bumps
 * `reloadKey`, which re-reads the list, and the poll resumes while anything
 * is still pending.
 */

export interface AddPageValues {
  url: string;
  brandId: number;
  /** "" = Default: `score_interval` is left out and the API picks (:184-185, :262). */
  interval: string;
  skus: string[];
}

type Busy = "request" | "toggle" | "generate" | "generateProduct";

/** A page action's busy key: one per action per page. */
const busyKey = (kind: Busy, id: number) => `${kind}:${id}`;

interface ProductPagesState {
  /** Oldest first. Null until the first load lands. */
  rows: PdpUrl[] | null;
  /** The first load (or a Try again) is out. */
  loading: boolean;
  /** The last hard load failed. */
  error: boolean;
  /** Try again: a hard reload. */
  reload: () => void;
  /** A silent re-read: rows from a load that started after the call (or a newer one), or null on a failure. */
  silentFetch: () => Promise<PdpUrl[] | null>;
  /**
   * Shows a write at once: merges `patch` into that page's row. For a write
   * the caller sent itself (the Edit dialog's PATCH). A load already out read
   * the server before the write, so it can't undo the patch; one landing
   * after it is re-read (loadSequence.ts). Sends nothing itself.
   */
  patchRow: (id: number, patch: Partial<PdpUrl>) => void;
  /** The one-at-a-time lock: the page whose sync Score now is out. */
  scoringPageId: number | null;
  /** When that click happened (ms) — the elapsed counter counts from it. */
  scoringStartedAt: number | null;
  /** Refusal lines by page id. */
  refusals: Record<number, string | null>;
  /** A page the add answered with `active === false`. */
  addedPausedId: number | null;
  /**
   * The page the last successful add created (the section opens its tab).
   * Set once the fresh list has landed, just before `add` resolves. If that
   * re-read failed it can name a page not in `rows` yet.
   */
  lastAddedId: number | null;
  isBusy: (kind: Busy, id: number) => boolean;
  /** An add POST is out: the header's Add a page pill holds, so the form can't close on it. */
  adding: boolean;
  score: (row: PdpUrl, sync: boolean) => void;
  toggleScoring: (row: PdpUrl) => void;
  add: (values: AddPageValues) => Promise<string | null>;
  deleteRow: (row: PdpUrl) => Promise<boolean>;
  unlinkVideo: (row: PdpUrl, link: PdpUrlVideoLink) => Promise<boolean>;
  generateJsonLd: (row: PdpUrl) => void;
  generateProductJsonLd: (row: PdpUrl) => void;
}

/**
 * A write that failed because the session is ending (`isSessionRedirect`):
 * the interceptor has already sent the browser to the login page
 * (api/index.ts:80-81, :135). Awaited in a catch, it never settles, so
 * nothing changes on screen — no toast, no refusal line, no re-read — and the
 * write's busy state (the Score now lock, "Deleting…", the form's add) holds
 * until the page is gone, as a hard load stays loading.
 */
const sessionEnding = (): Promise<never> =>
  new Promise<never>(() => {
    // Never settles.
  });

export const useProductPages = (desireid: number, reloadKey = 0, pollPaused = false): ProductPagesState => {
  const [rows, setRows] = useState<PdpUrl[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [scoringPageId, setScoringPageId] = useState<number | null>(null);
  const [scoringStartedAt, setScoringStartedAt] = useState<number | null>(null);
  const [refusals, setRefusals] = useState<Record<number, string | null>>({});
  const [addedPausedId, setAddedPausedId] = useState<number | null>(null);
  const [lastAddedId, setLastAddedId] = useState<number | null>(null);
  const { busy, markBusy: markBusyKey } = useBusyKeys();
  const [adding, setAdding] = useState(false);

  const mounted = useMountedRef();
  // The lock, readable synchronously: a double click must not send two POSTs.
  const scoringRef = useRef<number | null>(null);
  // After a sync score the API rebuilds the page's markup in the background;
  // watch the list until it lands (:189-193, :299-305). Cancelled on unmount.
  const markupWatchCancel = useRef<(() => void) | null>(null);
  useEffect(() => () => markupWatchCancel.current?.(), []);

  // One sequence per product: a new product starts from nothing, and the old
  // one's loads land nowhere. Disposed on unmount: a load asked for after
  // that sends nothing, and a patch shows nothing.
  const loads = useMemo(
    () =>
      createLoadSequence<PdpUrl>({
        fetch: async () => {
          // No brandId, as the modal (:211): staff list every brand's pages; a
          // brand user gets their own brands' (desirelist-api pdpUrls.js:117-126).
          const { data } = await axiosInstant.get(listProductPdpUrls(desireid));
          return Array.isArray(data) ? data : data?.data || [];
        },
        onRows: (next, landed) => {
          setRows(next);
          if (landed) {
            setError(false);
            setLoading(false);
          }
        },
        onHardFail: (err) => {
          // Mid login redirect: stay on the skeleton, with no "Couldn't load
          // product pages" flash before the login page.
          if (isSessionRedirect(err)) return;
          setError(true);
          setLoading(false);
        },
      }),
    [desireid],
  );
  useEffect(() => () => loads.dispose(), [loads]);

  useEffect(() => {
    setRows(loads.rows());
    setLoading(true);
    setError(false);
    loads.load(true);
  }, [loads, attempt]);

  // The page bumps `reloadKey` when the settings modal closes: a page added,
  // edited or removed there shows up here. In place — no skeleton — and only
  // on a changed key, never on mount.
  const seenReloadKey = useRef(reloadKey);
  useEffect(() => {
    if (seenReloadKey.current === reloadKey) return;
    seenReloadKey.current = reloadKey;
    loads.load();
  }, [reloadKey, loads]);

  const reload = useCallback(() => setAttempt((n) => n + 1), []);
  const silentFetch = useCallback(() => loads.load(), [loads]);

  // A pending attempt re-reads the list every 5s, and stops on its own once
  // nothing is pending — resolved, or stalled past 15 minutes (PdpScoreStatus.tsx:148-163).
  // Held while the settings modal polls the same list (`pollPaused`).
  usePendingScorePoll(pollPaused ? [] : rows || [], silentFetch, PENDING_POLL_MS);

  const patchRow = useCallback(
    (id: number, patch: Partial<PdpUrl>) =>
      loads.patch((list) => list.map((r) => (r.id === id ? { ...r, ...patch } : r))),
    [loads],
  );

  // A write can land after the page is left (a Score now takes 30–90s):
  // setRefusal skips the state write then, as markBusy does (useBusyKeys).
  const setRefusal = (id: number, message: string | null) => {
    if (!mounted.current) return;
    setRefusals((prev) => (prev[id] === message ? prev : { ...prev, [id]: message }));
  };
  const markBusy = (kind: Busy, id: number, on: boolean) => markBusyKey(busyKey(kind, id), on);
  const isBusy = useCallback((kind: Busy, id: number) => !!busy[busyKey(kind, id)], [busy]);

  // A failed Score now / Run report / Resume: a refusal is an inline line
  // (or, for ScoreAlreadyInProgress, nothing); anything else is the caller's toast.
  const refused = (id: number, e: any): boolean => {
    const refusal = refusalFor(e);
    if (!refusal) return false;
    if (refusal.kind === "line") setRefusal(id, refusal.message);
    return true;
  };

  // ---- Score now (sync) — handleRescore, :282-314 ----------------------------
  // Left the page mid-score: the sequence is disposed, so the patch and the
  // re-reads below send and show nothing, and no markup watch starts.
  const scoreNow = async (row: PdpUrl) => {
    // One sync score at a time: the endpoint runs one audit (:672).
    if (scoringRef.current != null) return;
    scoringRef.current = row.id;
    setScoringPageId(row.id);
    setScoringStartedAt(Date.now());
    setRefusal(row.id, null);
    const prevMarkup = markupSignature(row);
    try {
      const { data } = await axiosInstant.post(rescoreProductPdpUrl(desireid, row.id));
      const body = data?.latest_score ? data : data?.data;
      const fresh: PdpUrlScore | null = body?.latest_score || null;
      if (fresh) {
        // The response carries the list's latest_score / latest_attempt
        // shape, so the row is replaced wholesale (:290-296). The new score
        // id refetches that page's history (usePageScoreHistory).
        const latestAttempt: PdpUrlAttempt | null = body?.latest_attempt || null;
        patchRow(row.id, { latest_score: fresh, latest_attempt: latestAttempt });
      } else {
        // Awaited, so the lock drops only once the fresh rows land: the
        // block (and its tab's score) would otherwise fall back to "Not
        // scored yet" for a moment before the score shows.
        await silentFetch();
      }
      toast.success("Successfully rescored PDP URL!");
      markupWatchCancel.current?.();
      // Unmounted mid-score: the cleanup already ran, so a watch started now
      // would poll for 90s with nothing to cancel it.
      if (!mounted.current) return;
      markupWatchCancel.current = watchMarkupChange({
        rowId: row.id,
        prev: prevMarkup,
        load: silentFetch,
        onChange: () => toast.success("JSON-LD updated from the analysis"),
      });
    } catch (e: any) {
      if (isSessionRedirect(e)) await sessionEnding();
      if (!refused(row.id, e)) {
        toast.error(
          scoringLimitMessage(e) || e?.response?.data?.message || "Couldn't rescore this PDP URL. Please try again.",
        );
      }
      // A failed sync score answers 502 after writing its error attempt, never
      // a ready score (desirelist-api pdpUrls.js:498-504): this re-read puts
      // the block in failed-newer / failed-never from the data, with no reload
      // (:308-310), and the page's history has nothing new to fetch. Awaited
      // for the same reason as above: the lock (the "scoring" view) holds
      // until the error attempt is on the row — the re-read resolves on a
      // load that started after the 502, even when a poll overtakes it — so
      // the block goes straight to failed-newer / failed-never, with no "Not
      // scored yet" in between.
      await silentFetch();
    } finally {
      scoringRef.current = null;
      if (mounted.current) {
        setScoringPageId(null);
        setScoringStartedAt(null);
      }
    }
  };

  // ---- Run report (queued) — handleRequestScore, :320-345 --------------------
  const requestScore = async (row: PdpUrl) => {
    if (busy[busyKey("request", row.id)]) return;
    markBusy("request", row.id, true);
    setRefusal(row.id, null);
    try {
      const { data } = await axiosInstant.post(requestProductPdpUrlScore(desireid, row.id));
      const body = data?.latest_attempt ? data : data?.data;
      const latestAttempt: PdpUrlAttempt | null = body?.latest_attempt || null;
      // The pending attempt starts the 5s poll.
      if (latestAttempt) patchRow(row.id, { latest_attempt: latestAttempt });
      else silentFetch();
      toast.success("Report started — the score updates here when it finishes.");
    } catch (e: any) {
      if (isSessionRedirect(e)) await sessionEnding();
      if (!refused(row.id, e)) toast.error("Couldn't start the report. Please try again.");
      silentFetch();
    } finally {
      markBusy("request", row.id, false);
    }
  };

  const score = (row: PdpUrl, sync: boolean) => {
    if (sync) scoreNow(row);
    else requestScore(row);
  };

  // ---- Pause / resume — handleToggleScoring, :171-182 ------------------------
  const toggleScoring = async (row: PdpUrl) => {
    if (busy[busyKey("toggle", row.id)]) return;
    markBusy("toggle", row.id, true);
    setRefusal(row.id, null);
    try {
      await axiosInstant.patch(updateProductPdpUrl(desireid, row.id), { active: !row.active });
      toast.success(row.active ? "Scoring paused for this PDP URL." : "Scoring activated — it will be scored on its cadence.");
      // The button holds until the list shows the new state.
      await silentFetch();
    } catch (e: any) {
      if (isSessionRedirect(e)) await sessionEnding();
      // Resume can 409 ScoringLimitReached: the plan-limit line, inline.
      if (!refused(row.id, e)) {
        toast.error(scoringLimitMessage(e) || "Couldn't update scoring for this PDP URL. Please try again.");
      }
    } finally {
      markBusy("toggle", row.id, false);
    }
  };

  // ---- Add — handleAdd, :241-280 ---------------------------------------------
  // The form validated the URL and the owner (the modal's two toasts, :242-253,
  // as field errors). Resolves to an inline error, or null on success — once
  // the fresh list (with the new page) has landed and `lastAddedId` names it.
  const add = async ({ url, brandId, interval, skus }: AddPageValues): Promise<string | null> => {
    const before = loads.rows() || [];
    setAdding(true);
    try {
      const { data } = await axiosInstant.post(createProductPdpUrl(desireid), {
        pdp_url: url.trim(),
        brand_id: brandId,
        skus,
        ...(interval ? { score_interval: interval } : {}),
      });
      toast.success(skus.length ? "Successfully added PDP URL and SKUs!" : "Successfully added PDP URL!");
      const created = data?.id != null ? data : data?.data;
      // A full plan adds the page paused (pdpUrls.js createPdpUrl): the note.
      if (mounted.current && isAddedPaused(created, before)) setAddedPausedId(created.id);
      const fresh = await silentFetch();
      // Named after the list with the new page has landed, so its tab exists
      // when the section moves to it.
      const id = addedPageId(created, before, fresh);
      if (mounted.current && id != null) setLastAddedId(id);
      return null;
    } catch (e: any) {
      if (isSessionRedirect(e)) await sessionEnding();
      return intervalErrorMessage(e) || e?.response?.data?.message || "Couldn't add PDP URL. Please try again.";
    } finally {
      if (mounted.current) setAdding(false);
    }
  };

  // ---- Delete page — handleDeleteRow, :403-416 --------------------------------
  // Resolves true as soon as the DELETE succeeds, as the modal closes its
  // confirm before its re-read (:409-410). The page leaves the list at once —
  // a load already out can't bring it back (loadSequence.ts) — and the
  // re-read runs behind.
  const deleteRow = async (row: PdpUrl): Promise<boolean> => {
    try {
      await axiosInstant.delete(deleteProductPdpUrl(desireid, row.id));
      toast.success("Successfully deleted PDP URL and its SKUs!");
      loads.patch((list) => list.filter((r) => r.id !== row.id));
      silentFetch();
      return true;
    } catch (e: any) {
      if (isSessionRedirect(e)) await sessionEnding();
      toast.error(e?.response?.data?.message || "Couldn't delete this PDP URL. Please try again.");
      return false;
    }
  };

  // ---- Remove from page — handleUnlinkVideo, :420-433 --------------------------
  // As Delete page (:426-427): true at once, the link off the page's `videos`
  // at once, the re-read behind. The page, its SKUs, score and other videos stay.
  const unlinkVideo = async (row: PdpUrl, link: PdpUrlVideoLink): Promise<boolean> => {
    try {
      await axiosInstant.delete(productPdpUrlVideo(desireid, row.id, link.s3_file_id));
      toast.success("Video removed from this page");
      loads.patch((list) =>
        list.map((r) =>
          r.id === row.id
            ? { ...r, videos: (r.videos || []).filter((l) => Number(l.s3_file_id) !== Number(link.s3_file_id)) }
            : r,
        ),
      );
      silentFetch();
      return true;
    } catch (e: any) {
      if (isSessionRedirect(e)) await sessionEnding();
      toast.error(e?.response?.data?.message || "Couldn't remove this video from the page. Please try again.");
      return false;
    }
  };

  // ---- Video JSON-LD — handleGenerateJsonLd, :351-375 ------------------------
  // One call per video on the page, in sequence (each spends an LLM call),
  // then a re-read rather than a patch.
  const generateJsonLd = async (row: PdpUrl) => {
    const links = row.videos || [];
    if (links.length === 0 || busy[busyKey("generate", row.id)]) return;
    markBusy("generate", row.id, true);
    let failed = 0;
    let ending = false;
    try {
      await links.reduce<Promise<void>>(
        (chain, link) =>
          chain.then(async () => {
            // The session ended mid-run: the rest would fail the same way.
            if (ending) return;
            try {
              await axiosInstant.get(getFileIconikJsonLd(link.s3_file_id, { pdpUrlId: row.id, regenerate: true }));
            } catch (e) {
              if (isSessionRedirect(e)) ending = true;
              failed += 1;
            }
          }),
        Promise.resolve(),
      );
      if (ending) await sessionEnding();
      if (failed === 0) {
        toast.success(links.length === 1 ? "Successfully generated JSON-LD!" : `Generated JSON-LD for ${links.length} videos!`);
      } else if (failed < links.length) {
        toast.error(`Generated JSON-LD for ${links.length - failed} of ${links.length} videos.`);
      } else {
        toast.error("Couldn't generate JSON-LD. Please try again.");
      }
      await silentFetch();
    } finally {
      markBusy("generate", row.id, false);
    }
  };

  // ---- Product Object — handleGenerateProductJsonLd, :379-401 ----------------
  // Built from the product record, no video needed; the response carries the
  // blob and its validation, so the row is patched in place.
  const generateProductJsonLd = async (row: PdpUrl) => {
    if (busy[busyKey("generateProduct", row.id)]) return;
    markBusy("generateProduct", row.id, true);
    try {
      const { data } = await axiosInstant.post(generateProductPdpUrlJsonLd(desireid, row.id));
      const body = data?.product_json_ld ? data : data?.data;
      const current = (loads.rows() || []).find((r) => r.id === row.id) || row;
      patchRow(row.id, {
        product_json_ld: body?.product_json_ld ?? current.product_json_ld,
        product_json_ld_validation: body?.product_json_ld_validation ?? current.product_json_ld_validation,
      });
      toast.success("Product Object generated!");
    } catch (e: any) {
      if (isSessionRedirect(e)) await sessionEnding();
      toast.error(e?.response?.data?.message || "Couldn't generate the Product Object. Please try again.");
    } finally {
      markBusy("generateProduct", row.id, false);
    }
  };

  // The sync score's response IS the score: leaving the page loses it. Warn,
  // as the video page does (ScoreNowAction.tsx:92-101) — but never on the
  // way to the login page. A 401 on the Score now itself sends the browser
  // there (logoutUser clears storage, then sets location.href,
  // api/index.ts:102-117) while this lock still holds, and the score waits
  // on `sessionEnding`: a "Leave site?" answered with Stay would keep the
  // page, the lock and this prompt for good.
  useEffect(() => {
    if (scoringPageId == null) return undefined;
    const warn = (e: BeforeUnloadEvent) => {
      if (!localStorage.getItem(TOKEN)) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [scoringPageId]);

  return {
    rows,
    loading,
    error,
    reload,
    silentFetch,
    patchRow,
    scoringPageId,
    scoringStartedAt,
    refusals,
    addedPausedId,
    lastAddedId,
    isBusy,
    adding,
    score,
    toggleScoring,
    add,
    deleteRow,
    unlinkVideo,
    generateJsonLd,
    generateProductJsonLd,
  };
};
